import {
	Invoice,
	InvoiceDocumentType,
	InvoiceItem,
	InvoiceStatus,
	StockShortfall,
} from "../../types/finance/FinanceTypes";

/**
 * Presentation rules for an invoice, in one place so the list, the drawer and the rendered
 * document cannot disagree with each other.
 */

/**
 * What to print where the invoice number goes.
 *
 * ⚠️ `invoiceNumber` is NULL until the invoice is ISSUED — a draft does not consume a number.
 * Rendering the null as an empty cell reads as a loading bug, and rendering the raw UUID is
 * unusable, so a draft shows `Draft · <first 8 of id>`: short enough to read out over the phone,
 * unique enough to find the row.
 */
export function invoiceDisplayNumber(invoice: Pick<Invoice, "invoiceNumber" | "id">): string {
	if (invoice.invoiceNumber) {
		return invoice.invoiceNumber;
	}
	return `Draft · ${(invoice.id ?? "").slice(0, 8)}`;
}

/**
 * The tag colour + label for a status.
 *
 * ⚠️ Feed this `effectiveStatus`. The shared `StatusTag` map has no `sent` and no `overdue` row,
 * so both would fall through to an unstyled default tag showing the raw enum value — hence the
 * explicit colour/label pair here rather than `<StatusTag status={s} />` alone.
 */
export function invoiceStatusPresentation(status: InvoiceStatus | string): {
	color: string;
	label: string;
} {
	switch (status) {
		case InvoiceStatus.DRAFT:
			return { color: "var(--mg-info)", label: "Draft" };
		case InvoiceStatus.SENT:
			return { color: "var(--mg-primary)", label: "Issued" };
		case InvoiceStatus.PARTIALLY_PAID:
			return { color: "var(--mg-warning)", label: "Part paid" };
		case InvoiceStatus.PAID:
			return { color: "var(--mg-success)", label: "Paid" };
		case InvoiceStatus.OVERDUE:
			return { color: "var(--mg-danger)", label: "Overdue" };
		case InvoiceStatus.CANCELLED:
			return { color: "var(--mg-danger)", label: "Cancelled" };
		default:
			return { color: "var(--mg-info)", label: String(status ?? "") };
	}
}

/**
 * The document's own heading.
 *
 * ⚠️ **"Tax Invoice" ONLY when the server said `tax_invoice`.** The decision (does the clinic
 * have an ABN, is it GST-registered) is made once, server-side, and frozen onto the invoice at
 * issue. A draft has no `documentType` yet, so it is headed plainly — the safe direction, since
 * calling a document a tax invoice when it is not is the error that matters.
 */
export function invoiceDocumentTitle(invoice: Pick<Invoice, "documentType">): string {
	return invoice.documentType === InvoiceDocumentType.TAX_INVOICE ? "Tax Invoice" : "Invoice";
}

/**
 * Whether to render the GST summary block.
 *
 * `invoice` (the plain document type) means the clinic is not entitled to charge GST, so the
 * summary is suppressed outright. A draft has no decision yet and shows the summary only when
 * there is actually GST on it, so a GST-free clinic never sees a `$0.00 GST` line it cannot
 * explain to a patient.
 */
export function showsGstSummary(invoice: Pick<Invoice, "documentType" | "taxCents">): boolean {
	if (invoice.documentType === InvoiceDocumentType.INVOICE) {
		return false;
	}
	if (invoice.documentType === InvoiceDocumentType.TAX_INVOICE) {
		return true;
	}
	return Number(invoice.taxCents ?? 0) !== 0;
}

/** A draft can still be edited and issued; everything else is final or in flight. */
export function isDraft(invoice: Pick<Invoice, "status">): boolean {
	return invoice.status === InvoiceStatus.DRAFT;
}

/**
 * Whether a manual payment may be recorded. Mirrors the server guard: an issued invoice with a
 * balance owing, that is not imported.
 *
 * The client copy is a UX affordance only — the server re-derives the balance under a row lock,
 * which is the check that actually counts.
 */
export function canRecordPayment(invoice: Invoice): boolean {
	if (!invoice || invoice.source) {
		return false;
	}
	const settled =
		invoice.status === InvoiceStatus.PAID || invoice.status === InvoiceStatus.CANCELLED;
	return !isDraft(invoice) && !settled && Number(invoice.balanceDueCents ?? 0) > 0;
}

/** Cancelling is refused server-side for a paid, already-cancelled or imported invoice (409). */
export function canCancel(invoice: Invoice): boolean {
	return (
		!!invoice &&
		!invoice.source &&
		invoice.status !== InvoiceStatus.PAID &&
		invoice.status !== InvoiceStatus.CANCELLED
	);
}

/**
 * Whether the invoice can be emailed to the patient (`POST /invoices/:id/send`). Sending a draft
 * issues it and emails it in one; an already-issued, not-yet-settled invoice can be (re)sent.
 * Imported, paid and cancelled invoices are refused server-side.
 */
export function canSend(invoice: Invoice): boolean {
	return (
		!!invoice &&
		!invoice.source &&
		invoice.status !== InvoiceStatus.PAID &&
		invoice.status !== InvoiceStatus.CANCELLED
	);
}

// ── Stock shortfalls (S13) ────────────────────────────────────────────────────────────────────
//
// ⚠️ **Nothing here may gate anything.** Insufficient stock warns, allows and goes negative
// (parent decision D2) — the invoice is already issued, numbered and payable by the time any of
// this can be rendered. These functions produce text, and only text.
//
// There are two sources of the same fact and they are NOT interchangeable: the transient
// `ISSUE_WARNING_STOCK_SHORTFALL` on the issue/send response (present only on the delivery that
// actually ran), and the durable `metadata.stock_shortfalls` finance persists from that delivery.
// Read the warning for the immediate toast; read the metadata for anything shown afterwards —
// commerce answers a replayed delivery with `shortfalls: []` and cannot reconstruct them, so on a
// lost response the metadata is the only place the fact survives.

/**
 * The durable shortfall record off an invoice's metadata, defensively.
 *
 * `metadata` is free-form jsonb: an invoice issued before S10, or one whose delivery is still
 * pending, simply has no key here. A non-array value is treated as absent rather than trusted.
 */
export function readStockShortfalls(
	invoice: Pick<Invoice, "metadata"> | null | undefined,
): StockShortfall[] {
	const recorded = invoice?.metadata?.stock_shortfalls;

	if (!Array.isArray(recorded)) {
		return [];
	}

	return recorded.filter(
		(entry): entry is StockShortfall => !!entry && typeof entry === "object",
	);
}

/**
 * The line's own description, matched back from commerce's variant id.
 *
 * ⚠️ Matched on `sourceVariantId` ONLY, never on `sourceProductId`. Stock is keyed by variant and a
 * fulfilment is built from the lines themselves, so the variant match always hits for a real
 * shortfall; falling back to a product match would happily name a DIFFERENT variant's line on the
 * one invoice that has two of them. The raw ids are a worse label than a wrong one is a lie.
 */
function shortfallLineLabel(shortfall: StockShortfall, items: InvoiceItem[]): string {
	const line = items.find(
		(item) =>
			item.sourceVariantId != null &&
			Number(item.sourceVariantId) === Number(shortfall.variant_id),
	);

	return (
		line?.description?.trim() ||
		`Product ${shortfall.product_id} · variant ${shortfall.variant_id}`
	);
}

/**
 * One shortfall as a sentence a practitioner can act on:
 * `Vitamin D3 5000IU — 3 units requested, 1 available. Stock is now -2.`
 *
 * The resulting level is stated outright rather than "stock is now negative", because the number is
 * what a stocktake correction has to start from. `available` is the pre-consumption level, so it
 * can itself already be negative — an invoice issued against a shelf that was already overdrawn.
 */
export function formatStockShortfallWarning(
	shortfall: StockShortfall,
	items: InvoiceItem[] = [],
): string {
	const requested = Number(shortfall.requested ?? 0);
	const available = Number(shortfall.available ?? 0);
	const units = requested === 1 ? "unit" : "units";

	return `${shortfallLineLabel(shortfall, items)} — ${requested} ${units} requested, ${available} available. Stock is now ${available - requested}.`;
}

/**
 * Every recorded shortfall on an invoice, formatted. Empty for an invoice that had none — which is
 * the overwhelming majority, and is what the callers gate their rendering on.
 */
export function stockShortfallLines(
	invoice: Pick<Invoice, "metadata" | "items"> | null | undefined,
): string[] {
	const items = invoice?.items ?? [];

	return readStockShortfalls(invoice).map((shortfall) =>
		formatStockShortfallWarning(shortfall, items),
	);
}
